import { useQuery } from '@tanstack/react-query';
import { GlobalReactQueryOptions } from '../../config';
import { AgeCategory } from '../../../models/AgeCategory';
import {
    fetchLibxProductsFromCatalogue,
    fetchProductsFromCatalogue,
    fetchProductsFromLanguageLearning,
    fetchProductsFromLote
} from '../../api/productList/fetchProductsFromCatalogue';
import { ProductFilter, ProductSorting } from '../../../models/Product';
import { LoanFormat } from '../../../models/LoanFormat';
import { browserClient } from '../../react-query';

type UseProductsFromCatalogueOptions = {
    readonly fetchFunc?: typeof fetchProductsFromCatalogue;
    readonly ageCategory?: AgeCategory;
    readonly page?: number;
    readonly pageSize?: number;
    readonly sortBy?: ProductSorting;
    language?: string | undefined;
    shopLanguage: string | undefined;
};

const emptyProductsFromCatalogue = {
    total: 0,
    products: []
};

const CATALOGUE_QUERY_KEY_PREFIX = 'productsFromCatalogue';

/**
 * Get products for a specific loan format from the catalogue. It is possible to narrow the products by passing a
 * filter and pagination options.
 * Useful to populate product groups for example.
 */
export const useProductsFromCatalogueQuery = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    {
        fetchFunc = fetchProductsFromCatalogue,
        ageCategory,
        page,
        pageSize,
        sortBy,
        shopLanguage
    }: UseProductsFromCatalogueOptions = { shopLanguage: undefined }
) => {
    const params = {
        ageCategory,
        page,
        pageSize,
        sortBy,
        shopLanguage
    };

    const { data, ...result } = useQuery(
        [CATALOGUE_QUERY_KEY_PREFIX, loanFormat, filterMap, params],
        () => fetchFunc(loanFormat, filterMap, params),
        {
            ...GlobalReactQueryOptions
        }
    );

    return {
        ...result,
        data: data ?? emptyProductsFromCatalogue
    };
};

export function invalidateProductsFromCatalogue(loanFormat: LoanFormat) {
    return browserClient.invalidateQueries({ queryKey: [CATALOGUE_QUERY_KEY_PREFIX, loanFormat] });
}

const LOTE_QUERY_KEY_PREFIX = 'productsFromLote';

/**
 * Get products for a specific loan format from lote. It is possible to narrow the products by passing a
 * filter and pagination options.
 * Useful to populate product groups for example.
 */
export const useProductsFromLoteQuery = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    { fetchFunc = fetchProductsFromLote, ageCategory, page, pageSize, sortBy }: UseProductsFromCatalogueOptions = {
        shopLanguage: undefined
    }
) => {
    const params = {
        ageCategory,
        page,
        pageSize,
        sortBy
    };

    const { data, ...result } = useQuery(
        [LOTE_QUERY_KEY_PREFIX, loanFormat, filterMap, params],
        () => fetchFunc(loanFormat, filterMap, params),
        {
            ...GlobalReactQueryOptions
        }
    );

    return {
        ...result,
        data: data ?? emptyProductsFromCatalogue
    };
};

export function invalidateProductsFromLote(loanFormat: LoanFormat) {
    return browserClient.invalidateQueries({ queryKey: [LOTE_QUERY_KEY_PREFIX, loanFormat] });
}

const LANGUAGE_LEARNING_QUERY_KEY_PREFIX = 'productsFromLanguageLearning';
/**
 * Get products for a specific loan format from lote. It is possible to narrow the products by passing a
 * filter and pagination options.
 * Useful to populate product groups for example.
 */
export const useProductsFromLanguageLearningQuery = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    {
        fetchFunc = fetchProductsFromLanguageLearning,
        ageCategory,
        page,
        pageSize,
        sortBy,
        shopLanguage
    }: UseProductsFromCatalogueOptions = { shopLanguage: undefined }
) => {
    const params = {
        ageCategory,
        page,
        pageSize,
        sortBy,
        shopLanguage
    };

    const { data, ...result } = useQuery(
        [LANGUAGE_LEARNING_QUERY_KEY_PREFIX, loanFormat, filterMap, params],
        () => fetchFunc(loanFormat, filterMap, params),
        {
            ...GlobalReactQueryOptions
        }
    );

    return {
        ...result,
        data: data ?? emptyProductsFromCatalogue
    };
};

export function invalidateProductsFromLanguageLearning(loanFormat: LoanFormat) {
    return browserClient.invalidateQueries({ queryKey: [LANGUAGE_LEARNING_QUERY_KEY_PREFIX, loanFormat] });
}

export const useLibxProductsFromCatalogueQuery = (
    loanFormat: LoanFormat,
    filterMap?: ProductFilter,
    options: UseProductsFromCatalogueOptions = { shopLanguage: undefined }
) => {
    return useProductsFromCatalogueQuery(loanFormat, filterMap, {
        ...options,
        fetchFunc: fetchLibxProductsFromCatalogue
    });
};
